// Active-trip snapshot (T1.9 / D-20).
//
// When a saved trip is open, the chat pushes a COMPACT projection of it to the
// agent every message — the trip the user is looking at, as ground truth —
// instead of making the agent pull the whole object via get_trip each turn.
//
// "Compact" is the whole point: a month-long itinerary has 100+ activities, and
// the fat activity (place_id, coordinates, description, location text) would
// blow the context window on every turn, including turns that never touch the
// trip. So each activity is projected to the minimum the agent needs to REASON
// about the day and the impact of a change — name, time window, dwell, and the
// travel leg from the previous stop (so "move dinner earlier" can be answered
// with the knock-on travel time in mind). Ids, place_ids, coordinates, and long
// text are deliberately dropped; the agent pulls get_trip when it needs them
// (e.g. an activity_id to target an edit).
//
// This builds the STRUCTURED snapshot only. Rendering it to text, per-turn
// budgeting, and the "too many activities → show a count" overflow all live in
// the agent's context assembler (agents/context.py), so token policy has one
// owner. The UI just projects data it already holds in memory.

import { Activity, Trip } from './api/tripsApi';

export interface ActiveTripLeg {
  mode: string;             // chosen mode if the user overrode it, else computed
  min: number | null;       // travel minutes from the previous stop; null = no route
  km: number | null;
  warning?: 'far' | 'detour'; // server route-sanity flag
  crossDay?: boolean;
}

export interface ActiveTripActivity {
  date?: string;   // MM-DD-YYYY (matches the agent's action_request date format); absent = unscheduled
  start?: string;  // HH:MM 24h
  end?: string;    // HH:MM 24h
  name: string;
  dwellMin?: number;              // time spent at the stop (visitMinutes)
  leg?: ActiveTripLeg;            // inbound travel from the previous stop that day
  status: string;                 // 'scheduled' | 'planned'
  cost?: { amount: number; currency: string };
}

export interface ActiveTripSnapshot {
  id: string;
  title?: string;
  status?: string;
  // Dates normalised to MM-DD-YYYY so the agent can copy them straight into an
  // UPDATE_TRIP action_request.
  dates?: { startDate?: string; endDate?: string };
  destinations?: string[];
  travelers?: { adults: number; children: number };
  budget?: Trip['budget'];  // passed through as-is; the assembler renders it
  description?: string;
  updatedAt?: string;
  activityCount: number;
  activities: ActiveTripActivity[];
}

const pad = (n: number) => String(n).padStart(2, '0');

// ISO 8601 → MM-DD-YYYY, in the viewer's local zone so it matches exactly the
// day the trip screen shows for this activity (the day cards format the same
// Date the same way). Returns undefined for missing/unparseable input.
const isoToMdy = (iso?: string): string | undefined => {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${d.getFullYear()}`;
};

const isoToHm = (iso?: string): string | undefined => {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Trip dates are stored date-only (YYYY-MM-DD, no zone) — transform the string
// directly so we never shift the day across a timezone boundary.
const ymdToMdy = (ymd?: string): string | undefined => {
  if (!ymd) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd);
  return m ? `${m[2]}-${m[3]}-${m[1]}` : ymd;
};

const projectLeg = (leg: Activity['travelFromPrev']): ActiveTripLeg | undefined => {
  if (!leg) return undefined;
  const out: ActiveTripLeg = {
    mode: leg.chosenMode || leg.mode,
    min: typeof leg.minutes === 'number' ? leg.minutes : null,
    km: typeof leg.km === 'number' ? leg.km : null,
  };
  if (leg.warning) out.warning = leg.warning;
  if (leg.crossDay) out.crossDay = true;
  return out;
};

const projectActivity = (a: Activity): ActiveTripActivity => {
  const out: ActiveTripActivity = {
    name: a.title,
    status: a.status,
  };
  const date = isoToMdy(a.dateTime);
  if (date) out.date = date;
  const start = isoToHm(a.dateTime);
  if (start) out.start = start;
  const end = isoToHm(a.endDateTime);
  if (end) out.end = end;
  if (typeof a.visitMinutes === 'number') out.dwellMin = a.visitMinutes;
  const leg = projectLeg(a.travelFromPrev);
  if (leg) out.leg = leg;
  if (a.budget && typeof a.budget.amount === 'number') out.cost = a.budget;
  return out;
};

// Sortable key: MM-DD-YYYY → "YYYY-MM-DD HH:MM" for correct lexical ordering.
// null for unscheduled (dateless) stops, which sort after everything.
const sortKey = (a: ActiveTripActivity): string | null => {
  if (!a.date) return null;
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(a.date);
  const day = m ? `${m[3]}-${m[1]}-${m[2]}` : a.date;
  return `${day} ${a.start || '99:99'}`;
};

// Chronological; unscheduled last; stable within equal keys (Array.sort is
// stable). Plain string comparison — not localeCompare, whose punctuation
// handling reordered the unscheduled sentinel.
const bySchedule = (a: ActiveTripActivity, b: ActiveTripActivity): number => {
  const ka = sortKey(a);
  const kb = sortKey(b);
  if (ka === null && kb === null) return 0;
  if (ka === null) return 1;
  if (kb === null) return -1;
  return ka < kb ? -1 : ka > kb ? 1 : 0;
};

/**
 * Project a saved trip + its activities into the compact active_trip snapshot
 * pushed to the agent. `activities` must be the rich API Activity[] (which
 * carries visitMinutes / travelFromPrev / ISO times / structured cost) — NOT
 * the lossy per-screen view types.
 */
export const buildActiveTripSnapshot = (
  trip: Trip,
  activities: Activity[],
  opts?: { description?: string },
): ActiveTripSnapshot => {
  const projected = (activities || []).map(projectActivity);
  projected.sort(bySchedule);

  const snapshot: ActiveTripSnapshot = {
    id: String(trip.id),
    title: trip.title,
    status: trip.status,
    activityCount: projected.length,
    activities: projected,
  };

  const startDate = ymdToMdy(trip.dates?.startDate);
  const endDate = ymdToMdy(trip.dates?.endDate);
  if (startDate || endDate) snapshot.dates = { startDate, endDate };
  if (trip.destinations?.length) snapshot.destinations = trip.destinations;
  if (trip.travelers) snapshot.travelers = trip.travelers;
  if (trip.budget !== undefined && trip.budget !== null) snapshot.budget = trip.budget;
  const description = opts?.description ?? trip.description;
  if (description) snapshot.description = description;
  // updatedAt isn't in the Trip view type but the server includes it; capture it
  // when present so the agent can gauge how fresh this snapshot is.
  const updatedAt = (trip as { updatedAt?: string }).updatedAt;
  if (updatedAt) snapshot.updatedAt = updatedAt;

  return snapshot;
};
